import 'react-image-crop/dist/ReactCrop.css';

import { useState } from 'react';
import {
  faArrowUTurnUpLeft,
  faArrowUTurnUpRight,
  faCircleXmark,
  faSpinner,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import axios from 'axios';
import ReactCrop from 'react-image-crop';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Slider } from '@/components/ui/slider';
import { Heading5 } from '@/components/ui/typography';
import { showAiCreditsExhaustedModal, useAiCreditsExhausted } from '@/lib/ai-credits-gate';
import { transformImage } from './api';
import { AspectRatioGrid } from './aspect-ratio-grid';
import { useImageCropModal } from './hooks/use-image-crop-modal';
import { RotationControls } from './rotation-controls';
import type { ImageCropModalProps } from './types';

export function ImageCropModal({
  imageUrl,
  isOpen,
  onSave,
  onCancel,
  showUpscale = false,
  showRemoveBg = false,
  saveLabel = 'Save',
}: ImageCropModalProps) {
  const [isTransforming, setIsTransforming] = useState(false);
  const creditsExhausted = useAiCreditsExhausted();

  const {
    displayImgRef,
    crop,
    state,
    transformedImageUrl,
    isImageLoading,
    isTransforming: isCropTransforming,
    isSaving,
    selectedAspectRatio,
    rotationSliderValue,
    setRotationSliderValue,
    handleTransformedImageLoad,
    handleAspectRatioChange,
    handleRotate90,
    handleFlip,
    handleRevert,
    handleCropChange,
    handleCropComplete,
    handleSave,
    applyTransformUrl,
    undo,
    redo,
    canUndo,
    canRedo,
    onCancel: handleCancel,
  } = useImageCropModal({ imageUrl, isOpen, onSave, onCancel });

  const handleTransform = async (operation: 'upscale' | 'remove-bg') => {
    if (isTransforming) return;
    if (creditsExhausted) {
      showAiCreditsExhaustedModal();
      return;
    }
    setIsTransforming(true);
    try {
      applyTransformUrl(await transformImage(state.imageUrl, operation));
    } catch (error) {
      if (axios.isCancel(error)) return;

      toast.error('Image transform failed. Please try again.');
    } finally {
      setIsTransforming(false);
    }
  };

  const anyLoading = isSaving || isCropTransforming || isImageLoading || isTransforming;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent size="3xl" showCloseButton={false}>
        <DialogHeader className="sr-only">
          <DialogTitle>Crop Image</DialogTitle>
          <DialogDescription>
            Crop the image to the desired size and aspect ratio.
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={anyLoading} className="flex gap-8">
          <div className="w-50 shrink-0 space-y-6">
            <div className="space-y-2">
              <Heading5>Crop</Heading5>
              <AspectRatioGrid
                selectedRatio={selectedAspectRatio}
                onSelect={handleAspectRatioChange}
              />
            </div>

            <div className="space-y-2">
              <Heading5>Rotate</Heading5>
              <RotationControls onFlip={handleFlip} onRotate90={handleRotate90} />
              <Slider
                value={[rotationSliderValue]}
                min={state.baseRotation - 45}
                max={state.baseRotation + 45}
                step={1}
                onValueChange={(v) => setRotationSliderValue(v[0])}
                aria-label="Rotation angle"
                className="mt-6"
                disabled={isSaving}
              />
            </div>

            {(showUpscale || showRemoveBg) && (
              <div className="space-y-2">
                <Heading5>AI Enhancements</Heading5>
                <div className="flex flex-col gap-2">
                  {showUpscale && (
                    <Button
                      variant="outline"
                      colorScheme="secondary"
                      onClick={() => void handleTransform('upscale')}
                      className="w-full justify-start"
                    >
                      <Icon icon={faSparkles} className="size-4" />
                      Upscale
                    </Button>
                  )}
                  {showRemoveBg && (
                    <Button
                      variant="outline"
                      colorScheme="secondary"
                      onClick={() => void handleTransform('remove-bg')}
                      className="w-full justify-start"
                    >
                      <Icon icon={faCircleXmark} className="size-4" />
                      Remove BG
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative flex h-[500px] w-full items-center justify-center bg-muted">
            {(isImageLoading || !transformedImageUrl || isCropTransforming || isTransforming) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
                <Icon icon={faSpinner} className="size-12 animate-spin text-muted-foreground" />
              </div>
            )}
            {transformedImageUrl && (
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                onComplete={handleCropComplete}
                aspect={state.aspectRatio}
                className="max-h-[500px]"
              >
                <img
                  ref={displayImgRef}
                  src={transformedImageUrl}
                  alt="Crop preview"
                  onLoad={handleTransformedImageLoad}
                  className="block max-h-[500px] max-w-full"
                />
              </ReactCrop>
            )}
          </div>
        </fieldset>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleRevert}
            colorScheme="secondary"
            disabled={isSaving}
          >
            Revert to Original
          </Button>

          <IconTooltipButton tooltip="Undo" onClick={undo} disabled={!canUndo || isSaving}>
            <Icon icon={faArrowUTurnUpLeft} />
          </IconTooltipButton>
          <IconTooltipButton tooltip="Redo" onClick={redo} disabled={!canRedo || isSaving}>
            <Icon icon={faArrowUTurnUpRight} />
          </IconTooltipButton>

          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            disabled={isCropTransforming || isImageLoading || isTransforming}
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
